import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Counter from '@repo/ui/counter'
import { Column } from '@repo/ui/data-table'
import {
  BoxIcon,
  CalendarFourIcon,
  CalendarOneIcon,
  FilterIcon,
  PlusIcon,
  XIcon
} from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME, MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { createProductDate } from '../../api/products/create-product-date'
import { createProductTuitionDaysNumber } from '../../api/products/create-product-tuition-days-number'
import { getProduct } from '../../api/products/get-product'
import { getProductActions } from '../../api/products/get-product-actions'
import { getProductDates } from '../../api/products/get-product-dates'
import { updateProduct } from '../../api/products/update-product'
import DateChip from '../../components/elements/date-chip'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import { DynamicTableRef } from '../../components/elements/dynamic-table'
import {
  countFilters,
  getSelectedFilters
} from '../../components/elements/dynamic-table/utils/countFilters'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import NestedDataTable from '../../components/elements/nested-table'
import ProductDate from '../../components/pages/products/product-date'
import { ProductDateFilter } from '../../components/pages/products/product-date/product-date-filter'
import ProductDateForm from '../../components/pages/products/product-date-form'
import ProductForm from '../../components/pages/products/product-form'
import ProductJourneyPlacesTable from '../../components/pages/products/product-journey-places-table'
import ProductTuitionDaysNumberForm from '../../components/pages/products/product-tuition-days-number-form'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { useProductStore } from '../../lib/stores/use-product'
import { ProductTypeEnum } from '../../lib/types/enums/product/product-type.enum'
import { AcademicZoneEnum } from '../../lib/types/enums/staff/academic-zone.enum'
import { ProductDatesFilter } from '../../lib/types/models/product/product-date'
import { ProductTuitionDaysNumber } from '../../lib/types/models/product/product-tuition-days-number'
import {
  modalButtonConfig,
  updateButtonsConfig
} from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  productDateFilterMapping,
  transformProductDateFiltersToUrl,
  transformUrlToProductDateFilters
} from '../../lib/utils/filters-mapping/product-dates'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'
import { mapProductToInput } from './utils'

const productDatesInitialFilter: ProductDatesFilter = {
  years: [new Date().getFullYear()],
  options: [],
  periods: [],
  tripIds: [],
  journeyPlaces: []
}

const ProductTuitionDaysNumberColumns: Column<ProductTuitionDaysNumber>[] = [
  {
    accessorKey: 'from',
    enableSorting: true,
    header: 'De',
    cell: ({ row }) => <Badge text={`${row.original.from}j`} />
  },
  {
    accessorKey: 'to',
    enableSorting: true,
    header: 'À',
    cell: ({ row }) => <Badge text={`${row.original.to}j`} />
  },
  {
    accessorKey: 'durationInDays',
    enableSorting: true,
    header: '# de jours de cours',
    cell: ({ row }) => <Badge text={`${row.original.durationInDays}j`} />
  }
]

const UpdateProductPage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()
  const location = useLocation()
  const tableRef = useRef<DynamicTableRef<ProductTuitionDaysNumber>>(null)

  const comingFromJourney = useMemo(() => {
    return location.state?.ref === 'journey'
  }, [])
  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de produit invalide" />

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<ProductDatesFilter>({
      initialFilters: productDatesInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformProductDateFiltersToUrl,
      transformUrlToFilter: transformUrlToProductDateFilters
    })

  const { productDates, setProductDates, addProductDate } = useProductStore()

  const { modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'create-date' | 'create-product-tuition-days-number'
  >(['filters', 'create-date', 'create-product-tuition-days-number'])

  const { mutateAsync: updateProductMutation } = useMutation({
    mutationFn: updateProduct,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['product', id] })
      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['product-dates', id, selectedFilter]
      })
      toast.success('Produit modifié avec succès')
    }
  })

  const { data: product, isPending: isProductPending } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(+id)
  })

  const { data: productActions } = useQuery({
    queryKey: ['product-actions', +id],
    queryFn: () => getProductActions(+id)
  })

  // A One to One Individuel product has no tuition days numbers and no séjours, only stay places
  const isOneToOneIndividual = product?.type === ProductTypeEnum.ONE_TO_ONE_INDIVIDUAL

  const { isPending } = useQuery({
    enabled: !isOneToOneIndividual,
    queryKey: ['product-dates', id, selectedFilter],
    queryFn: async () => {
      const res = await getProductDates({
        filter: { productId: +id, ...selectedFilter },
        sort: { departureDate: 'asc' }
      })
      setProductDates(res.data)
      return res
    }
  })

  const { mutateAsync: createProductDateMutation } = useMutation({
    mutationFn: createProductDate,
    onSuccess: (data) => {
      addProductDate(data)
      toast.success('Date de produit créée avec succès')
    },
    onError: showAxiosError
  })

  const { mutateAsync: createProductTuitionNumberMutate } = useMutation({
    mutationFn: createProductTuitionDaysNumber,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create-product-tuition-days-number')
      toast.success('Nombre de jours de cours créé avec succès')
    }
  })

  const filtersLength = countFilters(filter)

  const displayedFilters = getSelectedFilters(selectedFilter)

  if (isProductPending) return <LoadingScreen />
  if (!product) return <NotFoundPage />
  if (!productActions) return <LoadingScreen />

  return (
    <div>
      <UserNavBar title={product.name} icon={BoxIcon} />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <div className={'flex items-center gap-3'}>
          <DateChip date={product.createdAt} label={'Créé'} />
          <DateChip date={product.updatedAt} label={'Mise à jour'} />
        </div>

        <ProductForm
          defaultValues={mapProductToInput(product)}
          canUpdateAccomodation={productActions.canUpdateAccomodation}
          canUpdateType={productActions.canUpdateType}
          buttonsConfig={updateButtonsConfig({
            onCancel: () =>
              back(
                comingFromJourney
                  ? Paths.JOURNEY_LIST_PAGE
                  : Paths.PRODUCT_UPDATE_PAGE.replace(':id', id)
              ),
            onUpdate: (values) => updateProductMutation({ id: product.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await updateProductMutation({ id: product.id, ...values })
              navigate(Paths.PRODUCT_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await updateProductMutation({ id: product.id, ...values })
              back(
                comingFromJourney
                  ? Paths.JOURNEY_LIST_PAGE
                  : Paths.PRODUCT_UPDATE_PAGE.replace(':id', id)
              )
            }
          })}
        />
      </Box>

      {isOneToOneIndividual && (
        <>
          <div className={'h-4'} />
          <ProductJourneyPlacesTable data={product} />
        </>
      )}

      {!isOneToOneIndividual && (
        <>
          <div className={'h-4'} />
          <Box>
            <NestedDataTable<ProductTuitionDaysNumber, null>
              ref={tableRef}
              variant="action"
              deleteMessage={'Voulez-vous vraiment supprimer ce nombre de jours de cours ?'}
              columns={ProductTuitionDaysNumberColumns}
              onDelete={() => toast.success('Nombre de jours de cours supprimé avec succès')}
              paginated
              limitControl
              actions={[
                {
                  icon: PlusIcon,
                  variant: 'outline',
                  label: 'Ajouter un nombre de jours de cours',
                  fn: () => toggleModal('create-product-tuition-days-number')
                }
              ]}
              title="Nombre de jours de cours par durée de séjour"
              endpoint={API.products.getProductTuitionDaysNumbers.url.replace(':productId', id)}
            />
          </Box>
          <div className={'h-4'} />
          <Box>
            <Typography variant={'sub-heading'} color={tailwindColors.grey.DEFAULT}>
              Liste des séjours
            </Typography>
            <div className={'h-4'} />
            <div className="flex justify-between">
              <Button
                variant={'primary'}
                icon={PlusIcon}
                onClick={() => toggleModal('create-date')}
                label="Créer une nouvelle date"
              />

              <div className="flex gap-2 flex-wrap">
                {displayedFilters
                  .filter((f) => f !== 'productId')
                  .map((f) => (
                    <Button
                      key={f}
                      variant={'tertiary'}
                      className="flex items-center justify-center gap-1"
                    >
                      <Typography variant="caption">{productDateFilterMapping[f]}</Typography>
                      <XIcon
                        color={tailwindColors.secondary.DEFAULT}
                        width="14px"
                        height="14px"
                        onClick={() => {
                          const newFilter = { ...filter }
                          delete newFilter[f]
                          onSelectedFilterChange(newFilter)
                        }}
                      />
                    </Button>
                  ))}

                <Button
                  variant={filter && filtersLength > 0 ? 'highlighted' : 'secondary'}
                  icon={FilterIcon}
                  label="Filtrer par"
                  onClick={() => toggleModal('filters')}
                >
                  <Counter number={filtersLength} />
                </Button>
              </div>
            </div>
            <div className={'h-4'} />

            {isPending && (
              <div className="flex justify-center h-36">
                <Loader size="md" />
              </div>
            )}
            <div className="flex flex-col gap-4">
              {productDates.map((productDate) => (
                <ProductDate key={productDate.id} productDate={productDate} />
              ))}
            </div>
          </Box>
        </>
      )}

      <Modal
        isOpen={modalState['create-date']}
        title="Date"
        icon={CalendarOneIcon}
        hideActions
        onCancel={() => toggleModal('create-date')}
      >
        <ProductDateForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            academicZones: Object.values(AcademicZoneEnum).filter(
              (zone) => zone !== AcademicZoneEnum.NONE
            )
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create-date'),
            onConfirm: async (values) => {
              await createProductDateMutation({ ...values, productId: product.id })
              toggleModal('create-date')
            }
          })}
        />
      </Modal>

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isPending}
        onClose={() => closeModel('filters')}
        onReset={() => {
          resetFilters()
          closeModel('filters')
        }}
        onApply={() => {
          onSelectedFilterChange({ ...filter })
          closeModel('filters')
        }}
        onFilterChange={onFilterChange}
      >
        <ProductDateFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      <Modal
        isOpen={modalState['create-product-tuition-days-number']}
        title={'Ajouter un nombre de jours de cours'}
        icon={CalendarFourIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create-product-tuition-days-number')}
      >
        <ProductTuitionDaysNumberForm
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createProductTuitionNumberMutate({ ...values, productId: +id })
            },
            onCancel: () => closeModel('create-product-tuition-days-number')
          })}
        />
      </Modal>
    </div>
  )
}

export default UpdateProductPage
